import cx from 'classnames';
import { FC, ReactNode, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { Badge, BadgeProps, BadgeSize } from '@shared/components/badge-new';
import { Flex } from '@shared/components/flex';
import { Link } from '@shared/components/link';
import { useAppUrl } from '@shared/contexts/ConfigContext';
import { ZapIcon } from '@shared/icons/zap';
import { checkIsActive } from '@shared/utils/common';
import { formatCountForBadge } from '@shared/utils/number';
import { getTransformedRoute } from '@shared/utils/route';

import { styles } from './SideNavigationItem.styles';

export interface SideNavigationItemProps extends WithStyles<typeof styles> {
  badge?: Omit<BadgeProps, 'classes'>;
  count?: number;
  startIcon: ReactNode;
  to: string;
  title: string;
}

const SideNavigationItemComponent: FC<SideNavigationItemProps> = ({ badge, count, classes, startIcon, to, title }) => {
  const { appUrl } = useAppUrl();
  const { pathname } = useLocation();

  const active = useMemo(() => {
    const transformedTo = getTransformedRoute(appUrl, to);
    return checkIsActive(pathname, transformedTo);
  }, [appUrl, pathname, to]);

  return (
    <Link
      classes={{
        root: classes.link,
        rootExternal: classes.externalLink,
        rootInternal: cx({ [classes.linkActive]: active }),
      }}
      to={to}
    >
      <Flex autoWidth={false} wrap="nowrap" classes={{ root: classes.linkContainer }}>
        <span className={classes.icon}>{startIcon}</span>
        <span className={classes.title}>{title}</span>
        {typeof count === 'number' && count > 0 && <span className={classes.count}>{formatCountForBadge(count)}</span>}
        {!!badge && (
          <Badge
            size={BadgeSize.small}
            startIcon={<ZapIcon classes={{ root: classes.badgeIcon }} />}
            {...badge}
            classes={{ badge: classes.badge }}
          />
        )}
      </Flex>
    </Link>
  );
};

export const SideNavigationItem = withStyles(styles)(SideNavigationItemComponent);
